import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency, formatCurrencyReadable, formatPrice, formatNumberToReadable, getChangeColorClass, getRealizedPercentageColorClass, parseFloatFromString } from '../utils/formatters';
import TokenAssignmentModal from './TokenAssignmentModal';
import Tooltip from './common/Tooltip';
import { toast } from 'react-hot-toast';
import { useMutation } from '@apollo/client';
import { ADD_TOKEN_TO_RECENT_PLAN, ADD_TOKEN_TO_PLAN } from '../graphql/mutations/allocationPlans';
import AddTokenModal from './allocation/AddTokenModal';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import TokenSparkline from './TokenSparkline';
import StockSparkline from './StockSparkline';
import DelistedIcon from './DelistedIcon';
import CompactTagDots from './tags/CompactTagDots';
import { tokenRoutePath } from '../utils/tokenRoutes';

// Local storage key for persisting last used allocation amount in Positions page
const POSITIONS_AMOUNT_LS_KEY = 'allocation_last_amount_positions';
const DEFAULT_POSITIONS_AMOUNT = 100;

const ChangeValue = ({ value }) => (
  <div className={`text-center text-[11px] leading-tight ${getChangeColorClass(value)}`}>
    {value || '—'}
  </div>
);

const CHANGE_PERIODS = [
  ['1h', 'priceChange1h'],
  ['24h', 'priceChange24h'],
  ['7d', 'priceChange7d'],
  ['30d', 'priceChange30d'],
  ['1y', 'priceChange1y']
];

const STOCK_CHANGE_PERIODS = [
  ['Day', 'priceChange24h'],
  ['7d', 'priceChange7d'],
  ['30d', 'priceChange30d'],
  ['1y', 'priceChange1y']
];

export const PositionMobileCard = ({ position, isExpanded = false, onToggle, assetKind = 'crypto' }) => {
  const isStock = assetKind === 'stock';
  const changePeriods = isStock ? STOCK_CHANGE_PERIODS : CHANGE_PERIODS;
  const token = position.token || {};
  const marketData = token.marketData || {};
  const path = tokenRoutePath(token);

  const totalCost = parseFloatFromString(position.investedCost || position.current?.basisCost);
  const proceeds = parseFloatFromString(position.current?.proceeds || 0);
  const realizedPercentage = totalCost > 0 ? (proceeds / totalCost * 100) : 0;
  const realizedDotColor = (() => {
    if (realizedPercentage >= 100) return 'bg-green-600 dark:bg-green-400';
    if (realizedPercentage >= 75) return 'bg-green-500 dark:bg-green-300';
    if (realizedPercentage >= 50) return 'bg-yellow-600 dark:bg-yellow-400';
    if (realizedPercentage >= 25) return 'bg-yellow-500 dark:bg-yellow-300';
    return 'bg-red-500 dark:bg-red-400';
  })();

  const score = token.riskScore?.score;
  const roundedRiskScore = score == null ? null : Math.round(score);
  let riskColorClass = 'text-gray-500 dark:text-gray-400';
  if (roundedRiskScore != null) {
    if (roundedRiskScore <= 20) riskColorClass = 'text-green-600 dark:text-green-400';
    else if (roundedRiskScore <= 40) riskColorClass = 'text-green-500 dark:text-green-300';
    else if (roundedRiskScore <= 60) riskColorClass = 'text-yellow-500 dark:text-yellow-400';
    else if (roundedRiskScore <= 80) riskColorClass = 'text-orange-500 dark:text-orange-400';
    else riskColorClass = 'text-red-500 dark:text-red-400';
  }

  const hasExitStrategy = position.exitStrategy?.levels?.length > 0;
  const hasInvalidation = Boolean(position.invalidationLevel);
  const strategyTone = (status) => {
    if (status === 'triggered') return 'text-red-500 border-red-500/40';
    if (status === 'approaching') return 'text-yellow-500 border-yellow-500/40';
    return 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600';
  };
  const stopToggle = (event) => event.stopPropagation();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle?.();
        }
      }}
      className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors cursor-pointer"
      aria-expanded={isExpanded}
    >
      <div className="flex min-h-[40px] items-center gap-2 text-left">
        <span className={`h-3 w-3 shrink-0 rounded-full ${realizedDotColor}`} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center text-sm leading-tight">
            {path ? (
              <Link
                to={path}
                onClick={stopToggle}
                className="min-w-0 truncate font-medium underline decoration-gray-400 underline-offset-2 text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                title={token.name}
              >
                {token.name}
              </Link>
            ) : (
              <span className="min-w-0 truncate font-medium text-gray-900 dark:text-white" title={token.name}>
                {token.name}
              </span>
            )}
            {path ? (
              <Link
                to={path}
                onClick={stopToggle}
                className="ml-1 shrink-0 underline decoration-gray-400 underline-offset-2 text-gray-500 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              >
                {token.ticker}
              </Link>
            ) : (
              <span className="ml-1 shrink-0 text-gray-500 dark:text-gray-300">{token.ticker}</span>
            )}
            {token.isDelisted && <DelistedIcon className="ml-1 h-3.5 w-3.5 shrink-0" />}
            {position.tags && position.tags.length > 0 && (
              <span className="ml-1 inline-flex shrink-0" onClick={stopToggle}>
                <CompactTagDots tags={position.tags} />
              </span>
            )}
          </div>
        </div>
        {!isExpanded && (
          <div className="shrink-0 whitespace-nowrap text-right text-[11px] leading-tight">
            <span className="text-gray-500 dark:text-gray-400">{isStock ? 'Day:' : '24h:'}</span>
            <span className={`ml-1 ${getChangeColorClass(marketData.priceChange24h)}`}>
              {marketData.priceChange24h || '—'}
            </span>
            {!isStock && (
              <>
                <span className="ml-3 text-gray-500 dark:text-gray-400">RS:</span>
                <span className={`ml-1 ${riskColorClass}`}>
                  {roundedRiskScore ?? '—'}
                </span>
              </>
            )}
          </div>
        )}
        <div className="shrink-0 text-right">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {formatCurrencyReadable(position.holdingsValue)}
          </div>
          <div className={`text-xs ${getChangeColorClass(position.current?.roi)}`}>
            {position.current?.roi || '—'}
          </div>
        </div>
        <span className="w-5 shrink-0 text-center text-base leading-none text-gray-400 dark:text-gray-500" aria-hidden="true">
          {isExpanded ? '⌃' : '⌄'}
        </span>
      </div>

      {isExpanded && (
        <div className="mt-2 rounded-md bg-gray-50 dark:bg-gray-700/50 px-2.5 py-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Value</div>
              <div className="text-gray-900 dark:text-gray-100">{formatCurrency(position.holdingsValue)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 dark:text-gray-400">Price</div>
              <div className="text-gray-900 dark:text-gray-100">{formatPrice(marketData.currentPrice)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Amount</div>
              <div className="truncate text-gray-900 dark:text-gray-100">{position.holdingsAmount} {token.ticker}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 dark:text-gray-400">P/L</div>
              <div className={getChangeColorClass(position.current?.pnl)}>{formatCurrency(position.current?.pnl)}</div>
            </div>
          </div>

          <div className="mt-2 rounded bg-white dark:bg-gray-800 px-2 py-1.5">
            <div className={`grid ${isStock ? 'grid-cols-4' : 'grid-cols-5'} gap-1 text-center text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400`}>
              {changePeriods.map(([label]) => (
                <div key={label}>{label}</div>
              ))}
            </div>
            <div className={`mt-0.5 grid ${isStock ? 'grid-cols-4' : 'grid-cols-5'} gap-1`}>
              {changePeriods.map(([label, key]) => (
                <ChangeValue key={label} value={marketData[key]} />
              ))}
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {position.percentage && (
              <span className="inline-flex items-center rounded bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-600 dark:text-gray-300">
                Size {position.percentage}
              </span>
            )}
            {roundedRiskScore != null && (
              <span className={`inline-flex items-center rounded bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[11px] ${riskColorClass}`}>
                Risk: {roundedRiskScore}
              </span>
            )}
            {hasExitStrategy && (
              <span className={`inline-flex items-center rounded border bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[11px] ${strategyTone(position.strategyStatus?.exitStatus)}`}>
                Exit
              </span>
            )}
            {hasInvalidation && (
              <span className={`inline-flex items-center rounded border bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[11px] ${strategyTone(position.strategyStatus?.invalidationStatus)}`}>
                Invalidation
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PositionRow = ({ position, onDataChange, planOptions = [], initialPlanId = null, refreshPlans, assetKind = 'crypto' }) => {
  const isStock = assetKind === 'stock';
  const navigate = useNavigate();
  const { currentPortfolioId } = usePortfolioContext();
  const token = position.token || {};
  const marketData = token.marketData || {};
  const [assignmentModal, setAssignmentModal] = useState({ isOpen: false, type: 'group' });
  const [showAddTokenModal, setShowAddTokenModal] = useState(false);
  const [positionsLastAmount, setPositionsLastAmount] = useState(DEFAULT_POSITIONS_AMOUNT);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [actionsMenuPosition, setActionsMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useRef(Symbol('rowMenu'));
  
  // Add mutation hook
  const [addTokenToRecentPlan] = useMutation(ADD_TOKEN_TO_RECENT_PLAN);
  const [addTokenToPlan] = useMutation(ADD_TOKEN_TO_PLAN);
  
  // Calculate realized percentage using parseFloatFromString
  const totalCost = parseFloatFromString(position.investedCost || position.current.basisCost);
  const proceeds = parseFloatFromString(position.current.proceeds || 0);
  const realizedPercentage = totalCost > 0 
    ? (proceeds / totalCost * 100) 
    : 0;
  
  // Get color class based on the percentage
  const realizedPercentageColor = getRealizedPercentageColorClass(realizedPercentage);
  
  // Function to handle refreshing the list after changes
  const handleDataChange = () => {
    // Call the parent's refetch function if provided
    if (typeof onDataChange === 'function') {
      onDataChange();
    }
  };
  
  // Handle link click - only navigate if not clicking on selectors
  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (assignmentModal.isOpen) e.preventDefault();
  };
  
  // Open assignment modal
  const handleSelectorClick = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setAssignmentModal({ isOpen: true, type });
  };

  const handleRowClick = () => {
    if (assignmentModal.isOpen || showAddTokenModal) return;
    const path = tokenRoutePath(token);
    if (path) {
      navigate(path);
    }
  };
  
  // Check if strategies exist
  const hasExitStrategy = position.exitStrategy && position.exitStrategy.levels && position.exitStrategy.levels.length > 0;
  const hasInvalidation = Boolean(position.invalidationLevel);
  
  // Get individual strategy statuses
  const exitStatus = position.strategyStatus?.exitStatus;
  const invalidationStatus = position.strategyStatus?.invalidationStatus;
  const exitTooltip = position.strategyStatus?.exitTooltip;
  const invalidationTooltip = position.strategyStatus?.invalidationTooltip;
  
  // Get color class for strategy indicator
  const getStrategyColorClass = (status) => {
    if (status === 'triggered') return 'text-red-500';
    if (status === 'approaching') return 'text-yellow-500';
    return 'text-gray-400 dark:text-gray-500';
  };
  
  // Handle adding token to allocation planner
  const handleAddToAllocationPlanner = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const saved = localStorage.getItem(POSITIONS_AMOUNT_LS_KEY);
      const parsed = parseFloatFromString(saved);
      if (!Number.isNaN(parsed) && parsed > 0) setPositionsLastAmount(parsed);
      else setPositionsLastAmount(DEFAULT_POSITIONS_AMOUNT);
    } catch (_) {
      setPositionsLastAmount(DEFAULT_POSITIONS_AMOUNT);
    }
    setShowAddTokenModal(true);
  };

  const openActionsMenuAtEvent = (e) => {
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 176; // approximate width in px
      const viewportPadding = 8;
      const estimatedMenuHeight = 120; // approximate height for 3 items
      let top = rect.bottom + 6; // default show below
      // If not enough space below, show above
      if (top + estimatedMenuHeight > (window.innerHeight - viewportPadding)) {
        top = Math.max(rect.top - 6 - estimatedMenuHeight, viewportPadding);
      }
      let left = rect.right - menuWidth; // align right edge
      // Clamp within viewport horizontally
      left = Math.min(
        Math.max(left, viewportPadding),
        (window.innerWidth - menuWidth - viewportPadding)
      );
      setActionsMenuPosition({ top, left });
      setIsActionsOpen(true);
    } catch (_) {
      setIsActionsOpen(true);
    }
  };

  useEffect(() => {
    const onScrollOrResize = () => setIsActionsOpen(false);
    const onGlobalClose = (e) => {
      if (e && e.detail && e.detail === menuId.current) return; // ignore self
      setIsActionsOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsActionsOpen(false);
    };
    const onMouseDown = (e) => {
      if (!isActionsOpen) return;
      const withinButton = buttonRef.current && buttonRef.current.contains(e.target);
      const withinMenu = menuRef.current && menuRef.current.contains(e.target);
      if (withinButton || withinMenu) return;
      setIsActionsOpen(false);
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize, true);
    window.addEventListener('pfan-close-row-menus', onGlobalClose);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
      window.removeEventListener('pfan-close-row-menus', onGlobalClose);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [isActionsOpen]);

  const handleConfirmAddToken = async (tokenSelection, amount, selectedPlanId) => {
    try { localStorage.setItem(POSITIONS_AMOUNT_LS_KEY, String(amount)); } catch (_) {}
    if (!currentPortfolioId) {
      toast.error('No portfolio selected. Please select a portfolio first.');
      return;
    }
    try {
      let data;
      if (selectedPlanId) {
        const res = await addTokenToPlan({
          variables: {
            planId: selectedPlanId,
            amount: String(amount || 0),
            tokenId: tokenSelection?.tokenId || token.id,
            coingeckoApiId: tokenSelection?.coingeckoApiId || token.coingeckoApiId
          }
        });
        // Handle GraphQL errors if not thrown
        if (res?.errors && res.errors.length > 0) {
          const dup = res.errors.find(e => (e.extensions && e.extensions.code === 'DUPLICATE_ALLOCATION_TOKEN') || (e.message || '').toLowerCase().includes('token is already in this allocation plan'));
          if (dup) {
            toast(`${token.name} is already in the allocation plan`,  { icon: '⚠️' });
            return;
          }
          toast.error('Failed to add to allocation planner');
          return;
        }
        data = { addTokenToRecentPlan: res?.data?.addTokenToPlan };
      } else {
        const res = await addTokenToRecentPlan({
          variables: {
            portfolioId: currentPortfolioId,
            tokenId: tokenSelection?.tokenId || token.id,
            amount: String(amount || 0)
          }
        });
        if (res?.errors && res.errors.length > 0) {
          const dup = res.errors.find(e => (e.extensions && e.extensions.code === 'DUPLICATE_ALLOCATION_TOKEN') || (e.message || '').toLowerCase().includes('token is already in this allocation plan'));
          if (dup) {
            toast(`${token.name} is already in the allocation plan`,  { icon: '⚠️' });
            return;
          }
          toast.error('Failed to add to allocation planner');
          return;
        }
        data = res?.data;
        // If a new plan was created inside addTokenToRecentPlan path, refresh plans
        try { refreshPlans?.(); } catch (_) {}
      }
      if (data?.addTokenToRecentPlan) {
        const plan = data.addTokenToRecentPlan;
        const createdDate = new Date(plan.createdAt);
        const now = new Date();
        const isNewPlan = (now.getTime() - createdDate.getTime()) < 5000;
        if (isNewPlan) {
          toast.success(`Created new plan "${plan.name}" with ${token.name}`);
          try { refreshPlans?.(); } catch (_) {}
        } else {
          toast.success(`Added ${token.name} to allocation planner`);
        }
      } else {
        toast.error('Failed to add to allocation planner');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding to allocation planner:', error);
      const msg = (error && (error.message || '')) || '';
      let isDup = msg.toLowerCase().includes('duplicate allocation token') || msg.toLowerCase().includes('already in this allocation plan') || msg.toLowerCase().includes('already exists');
      // Apollo graphQLErrors path
      const gqlErrs = error?.graphQLErrors || [];
      if (!isDup && Array.isArray(gqlErrs)) {
        isDup = gqlErrs.some(e => (e.extensions && e.extensions.code === 'DUPLICATE_ALLOCATION_TOKEN') || (e.message || '').toLowerCase().includes('already in this allocation plan'));
      }
      if (isDup) {
        toast((t) => (
          <div className="text-sm">
            <span className="font-medium">Already in plan:</span> {token.name}
          </div>
        ), { icon: '⚠️' });
      } else {
        toast.error('Failed to add to allocation planner');
      }
    } finally {
      setShowAddTokenModal(false);
    }
  };
  
  return (
    <>
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 group cursor-pointer" onClick={handleRowClick}>
      <td className="px-2  py-2 whitespace-nowrap text-sm">
        <div className="flex flex-col">
          {/* Token name and ticker - wrapped in Link */}
          <Link 
            to={tokenRoutePath(token)}
            className="flex items-center hover:text-blue-500"
            onClick={handleLinkClick}
          >
            <div className="min-w-0 w-full">
              <div className="flex items-center">
                <Tooltip
                  title={`Realized: ${realizedPercentage.toFixed(1)}% of total cost`}
                  position="top"
                  arrow={true}
                  animation="shift"
                  theme="dark"
                  size="small"
                  distance={10}
                >
                  <div className="mr-2 flex-shrink-0">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="currentColor" 
                      className={`w-3 h-3 ${realizedPercentageColor}`}
                    >
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </div>
                </Tooltip>
                <span className="text-gray-900 dark:text-white max-w-[100px] inline-block truncate" title={token.name}>
                  {token.name}
                </span>
                <span className="text-gray-500 dark:text-gray-300 ml-1">{token.ticker}</span>
                {token.isDelisted && (
                  <span className="ml-1 inline-flex items-center">
                    <DelistedIcon className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            </div>
          </Link>
          {position.tags && position.tags.length > 0 && (
            <CompactTagDots tags={position.tags} />
          )}
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
        {formatPrice(marketData.currentPrice)}
      </td>
      {!isStock && (
      <td className={`px-2 py-2 whitespace-nowrap text-xs text-right w-16 ${getChangeColorClass(marketData.priceChange1h)}`}>
        {marketData.priceChange1h}
      </td>
      )}
      <td className={`px-2 py-2 whitespace-nowrap text-xs text-right w-16 ${getChangeColorClass(marketData.priceChange24h)}`}>
        {marketData.priceChange24h}
      </td>
      <td className={`px-2 py-2 whitespace-nowrap text-xs text-right w-16 ${getChangeColorClass(marketData.priceChange7d)}`}>
        {marketData.priceChange7d ?? '—'}
      </td>
      <td className={`px-2 py-2 whitespace-nowrap text-xs text-right w-16 ${getChangeColorClass(marketData.priceChange30d)}`}>
        {marketData.priceChange30d ?? '—'}
      </td>
      <td className={`px-2 py-2 whitespace-nowrap text-xs text-right w-16 ${getChangeColorClass(marketData.priceChange1y)}`}>
        {marketData.priceChange1y ?? '—'}
      </td>
      {!isStock && (
      <>
      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
        {formatNumberToReadable(parseFloatFromString(marketData.volume24h || '0'))}
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
        {(() => {
          const mc = marketData.marketCap;
          const fdv = marketData.fullyDilutedValuation;
          if (mc && mc !== '0') return mc;
          if (fdv && fdv !== '0') return fdv;
          return '-';
        })()}
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {marketData.marketCapCategory}
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-sm text-center w-14">
        {(() => {
          const score = token.riskScore?.score;
          if (score == null) return null;
          const rounded = Math.round(score);
          let colorClass = 'text-gray-500 dark:text-gray-400';
          if (rounded <= 20) colorClass = 'text-green-600 dark:text-green-400';
          else if (rounded <= 40) colorClass = 'text-green-500 dark:text-green-300';
          else if (rounded <= 60) colorClass = 'text-yellow-500 dark:text-yellow-400';
          else if (rounded <= 80) colorClass = 'text-orange-500 dark:text-orange-400';
          else colorClass = 'text-red-500 dark:text-red-400';
          return <span className={`font-medium ${colorClass}`}>{rounded}</span>;
        })()}
      </td>
      </>
      )}
      <td className="px-3 py-2 text-sm min-w-[100px]">
        {isStock ? <StockSparkline stockId={token.id} stockName={token.name} className="w-36 h-8" /> : (
        <TokenSparkline 
          coingeckoId={token.coingeckoId} 
          tokenName={token.name}
          className="w-36 h-8"
        />
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
        <div className="flex justify-end items-center">
          <div>
            {formatCurrency(position.holdingsValue)}
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {position.holdingsAmount} {token.ticker}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
        <div>{position.percentage}</div>
        <div className="text-xs text-gray-400 dark:text-gray-500">
          Σ: {position.cumulativePercentage}
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
        <div className="text-right">
          <Tooltip
            title="Overall result: closed trades plus the current open position. Open the token page to compare this with the current holding ROI."
            position="top"
            arrow={true}
            theme="dark"
            size="small"
          >
            <div className={getChangeColorClass(position.current.pnl)}>
              {formatCurrency(position.current.pnl)}
            </div>
          </Tooltip>
          <div className={`text-xs ${getChangeColorClass(position.current.roi)}`}>
            {position.current.roi}
          </div>
        </div>
      </td>
      {!isStock && (
      <>
      <td className="px-3 py-2 whitespace-nowrap text-sm">
        <div className="flex justify-center items-center gap-1">
          {!hasExitStrategy && !hasInvalidation ? (
            <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
          ) : (
            <>
              {hasExitStrategy && (
                <Tooltip
                  title={exitTooltip || 'Exit strategy set'}
                  position="top"
                  arrow={true}
                  animation="shift"
                  theme="dark"
                  size="small"
                >
                  <span className={`text-xs font-medium cursor-default ${getStrategyColorClass(exitStatus)}`}>
                    E
                  </span>
                </Tooltip>
              )}
              {hasInvalidation && (
                <Tooltip
                  title={invalidationTooltip || 'Invalidation set'}
                  position="top"
                  arrow={true}
                  animation="shift"
                  theme="dark"
                  size="small"
                >
                  <span className={`text-xs font-medium cursor-default ${getStrategyColorClass(invalidationStatus)}`}>
                    I
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
        <div className="relative inline-block text-left">
          <button
            ref={buttonRef}
            className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isActionsOpen) {
                setIsActionsOpen(false);
              } else {
                window.dispatchEvent(new CustomEvent('pfan-close-row-menus', { detail: menuId.current }));
                openActionsMenuAtEvent(e);
              }
            }}
            aria-label="Row actions"
            title="Actions"
          >
            ···
          </button>
        </div>
        {isActionsOpen && createPortal(
          <div
            className="fixed z-[1000] w-44 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5"
            style={{ top: actionsMenuPosition.top, left: actionsMenuPosition.left }}
            ref={menuRef}
            onMouseDown={(e) => { /* keep open when interacting inside */ }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <div className="py-1">
              <button
                className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsActionsOpen(false); handleSelectorClick(e, 'group'); }}
              >
                Change group
              </button>
              <button
                className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsActionsOpen(false); handleAddToAllocationPlanner(e); }}
              >
                Add to allocation
              </button>
            </div>
          </div>, document.body)
        }
      </td>
      </>
      )}
    </tr>
    {assignmentModal.isOpen && (
      <TokenAssignmentModal
        isOpen={assignmentModal.isOpen}
        onClose={() => setAssignmentModal((m) => ({ ...m, isOpen: false }))}
        type={assignmentModal.type}
        tokenId={token.id}
        tokenTicker={token.ticker}
        currentItem={token.group}
        onApplied={handleDataChange}
      />
    )}
    {showAddTokenModal && (
      <AddTokenModal
        isOpen={showAddTokenModal}
        onClose={() => setShowAddTokenModal(false)}
        onConfirm={handleConfirmAddToken}
        allocations={[]}
        isEdit={true}
        presetTokenSelection={{ tokenId: token.id, coingeckoApiId: token.coingeckoApiId, name: token.name, ticker: token.ticker }}
        presetAmount={positionsLastAmount}
        allowedTokenSelection={{ tokenId: token.id, coingeckoApiId: token.coingeckoApiId }}
        focusAmountOnOpen={true}
        showPlanSelect={true}
        planOptions={planOptions}
        initialPlanId={initialPlanId}
        persistAmount={false}
        persistAmountKey={POSITIONS_AMOUNT_LS_KEY}
      />
    )}
    </>
  );
};

export default PositionRow; 

// Assignment Modal Mount
// Render just after the row to avoid layout issues